import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';
import ThaiAddressSearch from './ThaiAddressSearch';
import MapLocationPicker from './MapLocationPicker';
import EContractSection from './econtract/EContractSection';
import { useJourney, JourneyBar, ManualStepPanel } from './econtract/SoJourney';
import AssetTransferForm from './econtract/AssetTransferForm';
import { addMonthsIso, defaultContractTitle, rentalLines, rentalTotals } from './econtract/payload';

// <input type="date"> only understands YYYY-MM-DD. Anything longer (an ISO
// timestamp from an older response) is converted to the local calendar day.
const toDateInput = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const RentalOrderForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [soData, setSoData] = useState(null);

  const [formData, setFormData] = useState({
    // Customer
    customer_name: '',

    // Contact Person
    contact_name: '',
    contact_phone: '',
    contact_email: '',

    // Contract Details
    contract_name: '',
    contract_duration: 12,
    contract_start_date: '',
    contract_end_date: '',

    // Location
    same_as_customer_address: false,
    location_address_line1: '',
    location_address_line2: '',
    location_province: '',
    location_district: '',
    location_subdistrict: '',
    location_postal_code: '',
    location_latitude: 13.7563,
    location_longitude: 100.5018
  });

  // Wizard: step 1 = everything SaleOrder needs, step 2 = the extra fields
  // e-Contract needs (see ./econtract/EContractSection). One combined action
  // at the end of step 2.
  const { journey, busy: journeyBusy, reload: reloadJourney, toggle: toggleStep } = useJourney(id);
  // Which step's content is on screen. Starts on the step the SO is currently at.
  const [step, setStep] = useState(null);
  const goStep = (key) => { setStep(key); reloadJourney(); };

  useEffect(() => {
    fetchOrderDetails();
  }, [id]);

  // Auto-calculate end date when start date or duration changes. The last day
  // of the term is the day BEFORE the anniversary (same rule e-Contract uses
  // for rental_end_date) — see addMonthsIso.
  useEffect(() => {
    if (formData.contract_start_date && formData.contract_duration) {
      const end = addMonthsIso(formData.contract_start_date, formData.contract_duration);
      if (end) {
        setFormData(prev => ({ ...prev, contract_end_date: end }));
      }
    }
  }, [formData.contract_start_date, formData.contract_duration]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/rental/${id}`);

      if (response.data.success) {
        const orderData = response.data.order;
        setOrder(orderData);
        setSoData(orderData.soData);

        // Customer address from SO payload
        const customerAddr = orderData.soData?.data?.customerAddress || {};

        // Helper to extract Thai text, rejecting pure numbers
        const normalizeThaiName = (val) => {
          if (!val) return '';
          const s = String(val).trim();
          // Reject if it's purely digits (like "10", "1001", etc.)
          if (/^\d+$/.test(s)) return '';
          // Accept if it contains Thai characters
          if (/[ก-๙]/.test(s)) return s;
          // Otherwise return empty (non-Thai text)
          return '';
        };

        // Try to get Thai names from customerAddress, fallback to saved values
        const provinceName = normalizeThaiName(customerAddr.province || customerAddr.provinceName) ||
                            normalizeThaiName(orderData.location_province) || '';
        const districtName = normalizeThaiName(customerAddr.district || customerAddr.districtName) ||
                            normalizeThaiName(orderData.location_district) || '';
        const subdistrictName = normalizeThaiName(customerAddr.subdistrict || customerAddr.subdistrictName) ||
                               normalizeThaiName(orderData.location_subdistrict) || '';
        const postalCode = customerAddr.postcode || customerAddr.postalCode || orderData.location_postal_code || '';

        // Pre-fill form with existing data
        setFormData(prev => ({
          ...prev,
          customer_name: orderData.customer_name || orderData.soData?.data?.customerName || '',
          contact_name: orderData.contact_name || '',
          contact_phone: orderData.contact_phone || '',
          contact_email: orderData.contact_email || '',
          contract_name: orderData.contract_name || defaultContractTitle(orderData.soData) || orderData.soData?.data?.salesOrderSubject || '',
          contract_duration: orderData.contract_duration || 12,
          contract_start_date: toDateInput(orderData.contract_start_date),
          contract_end_date: toDateInput(orderData.contract_end_date),
          same_as_customer_address: orderData.same_as_customer_address || false,
          location_address_line1: orderData.location_address_line1 || customerAddr.address1 || customerAddr.address || '',
          location_address_line2: orderData.location_address_line2 || customerAddr.address2 || '',
          location_province: provinceName,
          location_district: districtName,
          location_subdistrict: subdistrictName,
          location_postal_code: orderData.location_postal_code || postalCode || '',
          location_latitude: parseFloat(orderData.location_latitude) || 13.7563,
          location_longitude: parseFloat(orderData.location_longitude) || 100.5018
        }));

      } else {
        toast.error('ไม่สามารถโหลดข้อมูลได้');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Moves the map pin to the picked area. Free OpenStreetMap geocoder, called only
  // when a person picks an address; if it can't find the place the pin simply stays.
  const geocodeAbort = React.useRef(null);
  const moveMapTo = async ({ subdistrict, district, province }) => {
    if (geocodeAbort.current) geocodeAbort.current.abort();
    const controller = new AbortController();
    geocodeAbort.current = controller;
    const attempts = [
      [subdistrict, district, province],
      [district, province],
      [province]
    ].map(parts => parts.filter(Boolean).join(' ')).filter(Boolean);
    for (const q of attempts) {
      try {
        const resp = await axios.get('https://nominatim.openstreetmap.org/search', {
          params: { q, format: 'jsonv2', countrycodes: 'th', limit: 1, 'accept-language': 'th' },
          signal: controller.signal
        });
        const hit = resp.data?.[0];
        if (hit) {
          setFormData(prev => ({ ...prev, location_latitude: parseFloat(hit.lat), location_longitude: parseFloat(hit.lon) }));
          return;
        }
      } catch (err) {
        return; // aborted by a newer pick, or offline — leave the pin where it is
      }
    }
  };

  const handleAddressSelect = (addr) => {
    setFormData(prev => ({
      ...prev,
      location_province: addr.province || '',
      location_district: addr.district || '',
      location_subdistrict: addr.subdistrict || '',
      location_postal_code: addr.zipcode || prev.location_postal_code || ''
    }));
    // The address dataset carries coordinates for most subdistricts; use them directly and
    // only fall back to the outside geocoder when it has none (e.g. Bangkok แขวง).
    if (addr.lat && addr.lng) {
      setFormData(prev => ({ ...prev, location_latitude: Number(addr.lat), location_longitude: Number(addr.lng) }));
    } else {
      moveMapTo({ subdistrict: addr.subdistrict, district: addr.district, province: addr.province });
    }
  };

  const handleLocationSelect = (lat, lng) => {
    setFormData(prev => ({
      ...prev,
      location_latitude: lat,
      location_longitude: lng
    }));
  };

  const handleSameAsCustomer = (e) => {
    const checked = e.target.checked;
    const addr = soData?.data?.customerAddress;
    setFormData(prev => ({
      ...prev,
      same_as_customer_address: checked,
      ...(checked && addr ? {
        location_address_line1: addr.address1 || prev.location_address_line1,
        location_address_line2: addr.address2 || prev.location_address_line2,
        location_province: addr.province || prev.location_province,
        location_district: addr.district || prev.location_district,
        location_subdistrict: addr.subdistrict || prev.location_subdistrict,
        location_postal_code: addr.postcode || prev.location_postal_code
      } : {})
    }));

    if (checked && !addr) {
      toast.info('ไม่พบที่อยู่ลูกค้า กรุณากรอกด้วยตัวเอง');
    } else if (checked) {
      moveMapTo({ subdistrict: addr.subdistrict, district: addr.district, province: addr.province });
    }
  };

  const handleSaveDraft = async () => {
    try {
      const response = await axios.post(`${API_URL}/rental/${id}/save`, formData);

      if (response.data.success) {
        toast.success('บันทึกความคืบหน้าเรียบร้อยแล้ว');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('เกิดข้อผิดพลาดในการบันทึก');
    }
  };

  const validateForm = () => {
    const required = [
      { field: 'contact_name', label: 'ชื่อผู้ติดต่อ' },
      { field: 'contact_phone', label: 'เบอร์โทรศัพท์' },
      { field: 'contract_name', label: 'ชื่อสัญญา' },
      { field: 'contract_duration', label: 'ระยะเวลาสัญญา' },
      { field: 'contract_start_date', label: 'วันที่เริ่มสัญญา' },
      { field: 'location_address_line1', label: 'ที่อยู่' },
      { field: 'location_province', label: 'จังหวัด' },
      { field: 'location_district', label: 'อำเภอ/เขต' },
      { field: 'location_subdistrict', label: 'ตำบล/แขวง' },
      { field: 'location_postal_code', label: 'รหัสไปรษณีย์' }
    ];

    for (const { field, label } of required) {
      if (!formData[field] || formData[field] === '') {
        toast.error(`กรุณากรอก${label}`);
        return false;
      }
    }

    if (formData.location_latitude === 0 || formData.location_longitude === 0) {
      toast.error('กรุณาเลือกตำแหน่งบนแผนที่');
      return false;
    }

    return true;
  };

  if (loading) {
    return <PageLoader message="กำลังโหลดข้อมูล..." />;
  }

  if (!order || !soData) {
    return (
      <div className="rental-form-error">
        <i className="bi bi-exclamation-triangle"></i>
        <p>ไม่พบข้อมูลคำสั่งซื้อ</p>
      </div>
    );
  }

  const so = soData.data || {};
  const active = step || journey?.current || 'so_received';
  const stepOf = (key) => journey?.steps?.find(x => x.key === key);
  const items = rentalLines(soData);
  const totals = rentalTotals(soData);
  const custAddr = so.customerAddress || {};
  const money = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  const rentalLine = items[0];
  const termLabel = formData.contract_start_date && formData.contract_end_date
    ? `${fmtDate(formData.contract_start_date)} → ${fmtDate(formData.contract_end_date)}`
    : 'ยังไม่ได้กำหนด';

  const stepOneActions = (
    <>
      <button type="button" onClick={handleSaveDraft} className="rs-btn rs-btn--ghost">
        <i className="bi bi-save"></i> บันทึกความคืบหน้า
      </button>
      <button type="button" onClick={() => goStep('asset_form')} className="rs-btn rs-btn--primary">
        ถัดไป: ขอโอนเป็นทรัพย์สิน <i className="bi bi-arrow-right"></i>
      </button>
    </>
  );

  return (
    <div className="rental-form rs">
      <nav className="rs-crumbs" aria-label="breadcrumb">
        <button type="button" onClick={() => navigate('/')}><i className="bi bi-house"></i> หน้าหลัก</button>
        <i className="bi bi-chevron-right"></i>
        <button type="button" onClick={() => navigate('/rental-so')}>เอกสารเครื่องเช่า</button>
        <i className="bi bi-chevron-right"></i>
        <span aria-current="page">{order.venio_so_number}</span>
      </nav>

      {order.is_test === 1 && (
        <div className="uat-banner" role="note">
          <i className="bi bi-flask"></i>
          <div>
            <b>โหมดทดสอบ</b> · ข้อมูลลูกค้าและเครื่องในออเดอร์นี้เป็นข้อมูลสมมติ แต่สัญญาจะถูกส่งไปที่ระบบ e-Contract (UAT) จริง
            เลขที่สัญญาขึ้นต้นด้วย TEST-DC · <Link to="/uat/rental">กลับไปหน้าทดสอบ</Link>
          </div>
        </div>
      )}

      <header className="rs-header">
        <div>
          <h1 className="rs-title">ใบเช่าเครื่อง {order.venio_so_number}</h1>
          {so.salesOrderSubject && <p className="rs-subtitle">{so.salesOrderSubject}</p>}
        </div>
        <div className="rs-header__meta">
          <span className="rs-chip rs-chip--amber"><i className="bi bi-hourglass-split"></i> รอดำเนินการ</span>
          <span className="rs-chip"><i className="bi bi-calendar3"></i> {fmtDate(so.salesOrderDate)}</span>
          {so.staffCode && <span className="rs-chip"><i className="bi bi-person-badge"></i> {so.staffCode}</span>}
        </div>
      </header>

      <JourneyBar journey={journey} active={active} onSelect={goStep} />

      {active === 'so_received' && (
      <div className="rs-layout">
        <div className="rs-main">

          {/* Customer — name/address come from the SO + customer master */}
          <section className="rs-card">
            <div className="rs-card__head">
              <div>
                <div className="rs-card__kicker">01 / CUSTOMER</div>
                <h2 className="rs-card__title">ลูกค้า</h2>
                <p className="rs-card__desc">ดึงจากใบสั่งขายใน Venio และข้อมูลลูกค้าในระบบ</p>
              </div>
            </div>

            <div className="rs-grid">
              <div className="rs-field rs-field--wide">
                <label htmlFor="customer_name">ชื่อลูกค้า</label>
                <input id="customer_name" type="text" name="customer_name" value={formData.customer_name}
                  onChange={handleInputChange} placeholder="ชื่อบริษัท / ลูกค้า" className="rs-input" />
              </div>
              <div className="rs-field">
                <label>รหัสลูกค้า</label>
                <input type="text" value={so.customerCode || ''} readOnly className="rs-input rs-input--readonly" />
              </div>
            </div>

            <div className="rs-subhead">ที่อยู่ลูกค้า (จากใบสั่งขาย)</div>
            <div className="rs-grid">
              <div className="rs-field rs-field--full">
                <label>ที่อยู่ บรรทัดที่ 1</label>
                <input type="text" readOnly className="rs-input rs-input--readonly"
                  value={custAddr.address1 || (!custAddr.address2 ? so.salesOrderCustomerAddress?.fullAddress || '' : '')} />
              </div>
              <div className="rs-field rs-field--full">
                <label>ที่อยู่ บรรทัดที่ 2</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={custAddr.address2 || ''} />
              </div>
              <div className="rs-field">
                <label>ตำบล / แขวง</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={custAddr.subdistrict || ''} />
              </div>
              <div className="rs-field">
                <label>อำเภอ / เขต</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={custAddr.district || ''} />
              </div>
              <div className="rs-field">
                <label>จังหวัด</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={custAddr.province || ''} />
              </div>
              <div className="rs-field">
                <label>รหัสไปรษณีย์</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={custAddr.postcode || ''} />
              </div>
            </div>

            <div className="rs-subhead">ผู้ติดต่อ</div>
            <div className="rs-grid">
              <div className="rs-field">
                <label htmlFor="contact_name">ชื่อผู้ติดต่อ <span className="rs-req">*</span></label>
                <input id="contact_name" type="text" name="contact_name" value={formData.contact_name}
                  onChange={handleInputChange} placeholder="ชื่อ-นามสกุล" className="rs-input" />
              </div>
              <div className="rs-field">
                <label htmlFor="contact_phone">เบอร์โทรศัพท์ <span className="rs-req">*</span></label>
                <input id="contact_phone" type="tel" name="contact_phone" value={formData.contact_phone}
                  onChange={handleInputChange} placeholder="0XX-XXX-XXXX" className="rs-input" />
              </div>
              <div className="rs-field">
                <label htmlFor="contact_email">อีเมล</label>
                <input id="contact_email" type="email" name="contact_email" value={formData.contact_email}
                  onChange={handleInputChange} placeholder="name@company.com" className="rs-input" />
              </div>
            </div>
          </section>

          {/* Machines on the SO */}
          <section className="rs-card">
            <div className="rs-card__head">
              <div>
                <div className="rs-card__kicker">02 / MACHINES</div>
                <h2 className="rs-card__title">รายการเครื่อง</h2>
                <p className="rs-card__desc">รายการสินค้าจากใบสั่งขาย (แก้ไขได้ใน Venio เท่านั้น)</p>
              </div>
            </div>

            <div className="rs-table-wrap">
              <table className="rs-table">
                <thead>
                  <tr>
                    <th>รหัสสินค้า</th>
                    <th>รายการ</th>
                    <th className="is-num">จำนวน</th>
                    <th className="is-num">ราคา/หน่วย</th>
                    <th className="is-num">รวม</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="rs-mono">{item.productCode}</td>
                      <td>{item.productName}</td>
                      <td className="is-num">{item.qty} {item.productUnitName || ''}</td>
                      <td className="is-num">{money(item.price)}</td>
                      <td className="is-num">{money(item.totalPrice ?? Number(item.price) * Number(item.qty))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totals.excluded > 0 && (
              <p className="rs-help" style={{ margin: '10px 0 0' }}>
                แสดงเฉพาะรายการเครื่องเช่า — ใบสั่งขายนี้มีรายการอื่นที่ไม่ใช่เครื่องเช่าอีก {totals.excluded} รายการ (ไม่รวมในสัญญาและยอดนี้)
              </p>
            )}
            <div className="rs-totals">
              <div><span>รวมก่อน VAT</span><span>{money(totals.sub)}</span></div>
              <div><span>VAT</span><span>{money(totals.vat)}</span></div>
              <div className="rs-totals__grand"><span>ยอดรวมสุทธิ</span><span>{money(totals.grand)} ฿</span></div>
            </div>
          </section>

          {/* Contract */}
          <section className="rs-card">
            <div className="rs-card__head">
              <div>
                <div className="rs-card__kicker">03 / CONTRACT</div>
                <h2 className="rs-card__title">รายละเอียดสัญญา</h2>
                <p className="rs-card__desc">ประเภทสัญญา การรับประกัน และระยะเวลา</p>
              </div>
            </div>

            <div className="rs-grid">
              <div className="rs-field rs-field--full">
                <label htmlFor="contract_name">ชื่อสัญญา <span className="rs-req">*</span></label>
                <input id="contract_name" type="text" name="contract_name" value={formData.contract_name}
                  onChange={handleInputChange} placeholder="เช่น สัญญาเช่าเครื่องซักพรม" className="rs-input" />
              </div>

              <div className="rs-field">
                <label htmlFor="contract_duration">ระยะเวลาสัญญา (เดือน) <span className="rs-req">*</span></label>
                <input id="contract_duration" type="number" name="contract_duration" min="1" value={formData.contract_duration}
                  onChange={handleInputChange} className="rs-input" />
              </div>
              <div className="rs-field">
                <label htmlFor="contract_start_date">วันที่เริ่มสัญญา <span className="rs-req">*</span></label>
                <input id="contract_start_date" type="date" name="contract_start_date" value={formData.contract_start_date}
                  onChange={handleInputChange} className="rs-input" />
              </div>
              <div className="rs-field">
                <label>วันที่สิ้นสุดสัญญา</label>
                <input type="date" name="contract_end_date" value={formData.contract_end_date} readOnly
                  className="rs-input rs-input--readonly" />
                <span className="rs-help">คำนวณอัตโนมัติ (วันก่อนครบรอบ)</span>
              </div>
            </div>
          </section>

          {/* Machine location */}
          <section className="rs-card">
            <div className="rs-card__head">
              <div>
                <div className="rs-card__kicker">04 / INSTALLATION</div>
                <h2 className="rs-card__title">สถานที่ติดตั้งเครื่อง</h2>
                <p className="rs-card__desc">ที่อยู่หน้างานและตำแหน่งบนแผนที่</p>
              </div>
            </div>

            <label className="rs-toggle" htmlFor="same_as_customer">
              <input type="checkbox" id="same_as_customer" name="same_as_customer_address"
                checked={formData.same_as_customer_address} onChange={handleSameAsCustomer} />
              <span>ใช้ที่อยู่เดียวกับลูกค้า</span>
            </label>

            <div className="rs-grid">
              <div className="rs-field rs-field--full">
                <label htmlFor="location_address_line1">ที่อยู่ บรรทัดที่ 1 <span className="rs-req">*</span></label>
                <input id="location_address_line1" type="text" name="location_address_line1" value={formData.location_address_line1}
                  onChange={handleInputChange} placeholder="เลขที่, หมู่, ซอย, ถนน" className="rs-input" />
              </div>
              <div className="rs-field rs-field--full">
                <label htmlFor="location_address_line2">ที่อยู่ บรรทัดที่ 2</label>
                <input id="location_address_line2" type="text" name="location_address_line2" value={formData.location_address_line2}
                  onChange={handleInputChange} placeholder="ข้อมูลเพิ่มเติม (ถ้ามี)" className="rs-input" />
              </div>
              <div className="rs-field rs-field--full">
                <label>ตำบล / อำเภอ / จังหวัด <span className="rs-req">*</span></label>
                <ThaiAddressSearch
                  value={{
                    province: formData.location_province,
                    district: formData.location_district,
                    subdistrict: formData.location_subdistrict
                  }}
                  onSelect={handleAddressSelect}
                />
                <span className="rs-help">เลือกจากรายการ — ระบบจะเติมตำบล อำเภอ จังหวัด รหัสไปรษณีย์ และย้ายแผนที่ให้อัตโนมัติ</span>
              </div>
              <div className="rs-field">
                <label>ตำบล/แขวง</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={formData.location_subdistrict} />
              </div>
              <div className="rs-field">
                <label>อำเภอ/เขต</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={formData.location_district} />
              </div>
              <div className="rs-field">
                <label>จังหวัด</label>
                <input type="text" readOnly className="rs-input rs-input--readonly" value={formData.location_province} />
              </div>
              <div className="rs-field">
                <label htmlFor="location_postal_code">รหัสไปรษณีย์ <span className="rs-req">*</span></label>
                <input id="location_postal_code" type="text" name="location_postal_code" value={formData.location_postal_code}
                  onChange={handleInputChange} placeholder="10XXX" maxLength="5" className="rs-input" />
              </div>
            </div>

            <div className="rs-map">
              <MapLocationPicker
                latitude={formData.location_latitude}
                longitude={formData.location_longitude}
                onLocationSelect={handleLocationSelect}
                disabled={false}
              />
            </div>
          </section>

          <div className="rs-actionbar rs-actionbar--inline">{stepOneActions}</div>
        </div>

        {/* Sticky summary: what this order is, at a glance */}
        <aside className="rs-aside">
          <div className="rs-summary">
            <div className="rs-summary__label">สรุปคำสั่งเช่า</div>
            <div className="rs-summary__amount">{money(totals.grand)} <small>฿</small></div>
            <div className="rs-summary__sub">รวม VAT {Number(items[0]?.vatTaxValue ?? 7)}%</div>

            <dl className="rs-summary__list">
              <div><dt>ลูกค้า</dt><dd>{formData.customer_name || so.customerCode || '—'}</dd></div>
              <div><dt>เครื่อง</dt><dd>{rentalLine ? `${rentalLine.productName}${items.length > 1 ? ` +${items.length - 1}` : ''}` : '—'}</dd></div>
              <div><dt>ระยะเวลา</dt><dd>{formData.contract_duration} เดือน</dd></div>
              <div><dt>ช่วงสัญญา</dt><dd>{termLabel}</dd></div>
              <div><dt>เงื่อนไขชำระ</dt><dd>{so.paymentTerm || '—'}</dd></div>
            </dl>

            <div className="rs-summary__actions">{stepOneActions}</div>
          </div>
        </aside>
      </div>
      )}

      {active === 'asset_form' && (
        <ManualStepPanel step={stepOf('asset_form') && { ...stepOf('asset_form'), link: null }} title="ขอโอนเครื่องเป็นทรัพย์สิน" busy={journeyBusy}
          toggleLabel={['ส่งทางอื่นแล้ว', 'ยกเลิกการทำเครื่องหมาย']}
          onToggle={async (st) => { const r = await toggleStep(st); if (r && st.state !== 'done') setStep(r.current); }}
          onNext={() => goStep('asset_adjust')}>
          <p>เครื่องที่ให้เช่าต้องเป็น <b>ทรัพย์สิน</b> ไม่ใช่สินค้า ตรวจข้อมูลที่กรอกให้แล้ว เลือกสโตร์และผู้ลงนาม แล้วกดส่ง</p>
          <AssetTransferForm orderId={id} onSubmitted={() => { reloadJourney(); }} />
        </ManualStepPanel>
      )}

      {active === 'asset_adjust' && (
        <ManualStepPanel step={stepOf('asset_adjust')} title="รออนุมัติโอนเครื่องเป็นทรัพย์สิน" busy={journeyBusy}
          onToggle={async (st) => { const r = await toggleStep(st); if (r && st.state !== 'done') setStep(r.current); }}
          onNext={() => goStep('contract')}>
          <p>ฝ่ายที่เกี่ยวข้องอนุมัติการโอน (ทำนอกระบบ VERP) เมื่ออนุมัติแล้ว <b>เครื่องจะมี Serial</b> และเลือกใส่ในสัญญาได้</p>
          <p className="rs-help">อนุมัติแล้วให้กด "ทำเรียบร้อยแล้ว"</p>
        </ManualStepPanel>
      )}

      {active === 'signed' && (
        <section className="rs-card sj-panel">
          <div className="sj-panel__head">
            <h2 className="rs-card__title">รอลงนามสัญญา</h2>
            <span className={`sj__state sj__state--${stepOf('signed')?.state || 'todo'}`}>
              {{ done: 'เสร็จแล้ว', doing: 'กำลังดำเนินการ', todo: 'ยังไม่ทำ', problem: 'มีปัญหา' }[stepOf('signed')?.state || 'todo']}
            </span>
          </div>
          <div className="sj-panel__body">
            <p>{stepOf('contract')?.state === 'done'
              ? 'ส่งสัญญาไปที่ e-Contract แล้ว รอผู้อนุมัติของ Peerapat และลูกค้าลงนาม ไม่ต้องทำอะไรเพิ่ม'
              : 'ยังไม่ได้ส่งสัญญา ไปที่ขั้นตอน "ทำสัญญา" ก่อน'}</p>
          </div>
          <div className="sj-panel__actions">
            <button type="button" className="rs-btn rs-btn--ghost" onClick={() => goStep('contract')}>ดูสถานะสัญญา</button>
          </div>
        </section>
      )}

      {/* The contract form stays mounted (just hidden) so switching steps doesn't throw
          away fields that haven't been sent or saved yet. */}
      <div style={{ display: active === 'contract' ? 'block' : 'none' }}>
        {stepOf('asset_adjust') && stepOf('asset_adjust').state !== 'done' && order.is_test !== 1 && (
          <div className="uat-banner" role="note" style={{ marginBottom: 14 }}>
            <i className="bi bi-info-circle"></i>
            <div>ยังไม่ได้ทำเครื่องหมายว่าอนุมัติโอนเป็นทรัพย์สินแล้ว ถ้ายังไม่อนุมัติ จะยังไม่มี Serial ให้เลือก
              {' '}<button type="button" className="econ-link-btn" onClick={() => goStep('asset_form')}>กลับไปขั้นตอนก่อนหน้า</button></div>
          </div>
        )}
        <EContractSection
          orderId={id}
          order={order}
          soData={soData}
          formData={formData}
          validateStep1={validateForm}
          saveStep1={() => axios.post(`${API_URL}/rental/${id}/save`, formData)}
          onBack={() => goStep('asset_adjust')}
        />
      </div>
    </div>
  );
};

export default RentalOrderForm;
